'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './CanonicalUrlCheckerContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

const steps = [
  {
    number: '1',
    title: 'Find your canonical tag',
    description:
      'Open your page in a browser and view the page source. Search for rel="canonical" in the HTML head section. Copy the href value. If no canonical tag exists, that is an issue this checker will help you address.',
  },
  {
    number: '2',
    title: 'Enter both URLs',
    description:
      'Paste the page URL as it appears in the browser address bar, and the canonical URL from your canonical tag. Both must be absolute URLs starting with https://.',
  },
  {
    number: '3',
    title: 'Review all 6 checks',
    description:
      'Instantly see whether your URLs match on protocol, host, trailing slash, path case, query parameters, and exact match. Each check shows a pass or fail with a clear explanation of what to fix.',
  },
];

const canonicalIssues = [
  { issue: 'www vs non-www', status: 'Mismatch', example: 'Page: example.com | Canonical: www.example.com', fix: 'Choose one www preference site-wide. Redirect the other with a 301.' },
  { issue: 'Trailing slash', status: 'Minor', example: 'Page: /page | Canonical: /page/', fix: 'Standardize trailing slash usage and apply 301 redirects consistently.' },
  { issue: 'HTTP vs HTTPS', status: 'Mismatch', example: 'Page: https:// | Canonical: http://', fix: 'Use HTTPS everywhere. Update canonical tags to https:// versions.' },
  { issue: 'URL case', status: 'Minor', example: 'Page: /Blog/Post | Canonical: /blog/post', fix: 'Use lowercase URLs throughout. Redirect uppercase variants with 301s.' },
  { issue: 'Query parameters', status: 'Warning', example: 'Canonical: /page?ref=email', fix: 'Canonical should point to the clean URL without tracking parameters.' },
  { issue: 'Cross-domain canonical', status: 'Intentional', example: 'Canonical points to another domain', fix: 'Valid for syndicated content. Verify the canonical domain is correct.' },
];

const canonicalMistakes = [
  {
    icon: '🔀',
    title: 'Using relative canonical URLs',
    description:
      'Some CMS platforms generate canonical tags with relative paths like /page instead of full absolute URLs like https://www.example.com/page. Relative canonicals are ambiguous for search engines and may not be processed correctly, particularly when content is syndicated or accessed from different entry points.',
    stat: 'Always use absolute URLs in canonical tags',
  },
  {
    icon: '🔗',
    title: 'Canonical chains',
    description:
      'A canonical chain occurs when page A canonicalizes to page B, which canonicalizes to page C. Google will follow the chain but the canonical signal weakens at each hop. If you discover a canonical chain, flatten it so all variants point directly to the final preferred URL.',
    stat: 'Canonical tags should point directly to the final preferred URL',
  },
  {
    icon: '🚫',
    title: 'Canonicalizing to a noindex page',
    description:
      'If your canonical URL points to a page with a noindex tag, you are telling Google to consolidate link signals to a page you do not want indexed. This creates a contradictory signal that Google will likely resolve by ignoring your canonical instruction.',
    stat: 'Your canonical target page must be indexable',
  },
  {
    icon: '📄',
    title: 'Missing canonical on paginated pages',
    description:
      'Paginated series like /blog?page=2 and /blog?page=3 need canonical handling. Without canonical tags, each pagination page may compete with the main page. Either self-canonicalize each pagination page or consolidate to the first page using canonical or noindex on deeper pages.',
    stat: 'Handle all paginated pages with explicit canonical strategy',
  },
  {
    icon: '⚡',
    title: 'Canonical loaded by JavaScript',
    description:
      'If your canonical tag is injected by JavaScript rather than server-rendered in the HTML head, Google may not discover it during the initial crawl. Canonical tags must be in the static HTML head section to be reliably processed by all search engine crawlers.',
    stat: 'Canonical tags must be in static server-rendered HTML',
  },
  {
    icon: '🔁',
    title: 'Conflicting canonical and redirect signals',
    description:
      'When a page has a 301 redirect to URL A but a canonical tag pointing to URL B, the signals conflict. Google resolves these conflicts, but it creates uncertainty that can delay correct indexing. Align your canonical tags and redirect logic so they both point to the same preferred URL.',
    stat: 'Canonical tags and redirects must point to the same URL',
  },
];

const tips = [
  {
    number: '01',
    title: 'Use accordions to build richer canonical pages',
    description:
      'Instead of creating multiple thin pages that require complex canonical decisions, consolidate related information into accordion sections on one strong canonical page. Each accordion panel adds content depth without splitting ranking signals across URLs.',
    cta: 'Try Accordion widget',
    ctaHref: '/widgets/accordion',
  },
  {
    number: '02',
    title: 'Use tabs to avoid near-duplicate pages',
    description:
      'Tabs let you present multiple variations of content on one canonical URL. Instead of creating separate pages for each product variant or comparison, use tabs to consolidate that content and point all canonical tags to one clean page.',
    cta: 'Try Tabs widget',
    ctaHref: '/widgets/tabs',
  },
  {
    number: '03',
    title: 'Consolidate comparison content on one canonical page',
    description:
      'Comparison pages are a common source of canonical confusion because similar comparisons appear on multiple pages. Use comparison table widgets to consolidate all variants into one authoritative canonical page that captures the full ranking potential.',
    cta: 'Try Comparison Tables widget',
    ctaHref: '/widgets/comparison-tables',
  },
  {
    number: '04',
    title: 'Keep canonical pages fresh with content feeds',
    description:
      'Static canonical pages can lose ranking momentum over time. Adding a content feed keeps your canonical pages dynamically updated with relevant content, signaling active maintenance to search engines without creating new duplicate URLs.',
    cta: 'Try Feeds widget',
    ctaHref: '/widgets/feeds',
  },
  {
    number: '05',
    title: 'Set www preference in Google Search Console',
    description:
      'Google Search Console allows you to set a preferred domain (www or non-www). Set your preference there and ensure all canonical tags, internal links, and sitemaps use the same version. This is the most reliable way to establish canonical domain authority.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '06',
    title: 'Self-canonicalize every indexable page',
    description:
      'Every page you want indexed should have a self-referencing canonical tag that points to its own URL. This practice is a proactive defense against future duplication caused by URL parameters, session IDs, or syndication.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '07',
    title: 'Audit canonical tags after site migrations',
    description:
      'Domain migrations, HTTPS transitions, and CMS platform changes often break canonical tags silently. Run a full canonical audit immediately after any site migration and again 4 to 6 weeks later to catch delayed issues.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'Monitor canonical coverage in Google Search Console',
    description:
      'The URL Inspection tool in Google Search Console shows you the canonical Google has chosen for any page. If it differs from your declared canonical, there is a signal conflict worth investigating. Check high-value pages regularly.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'Canonical Tag',
    definition: 'An HTML link element in the page head that declares the preferred version of a URL. Tells search engines which version of a page to index and credit with ranking signals.',
    formula: '<link rel="canonical" href="URL">',
    whenToUse: 'Every indexable page. Required for any site with duplicate or near-duplicate content.',
  },
  {
    term: 'Duplicate Content',
    definition: 'Substantially similar or identical content accessible at multiple URLs. Can dilute link equity, split rankings, and cause the wrong page version to appear in search results.',
    formula: 'Qualitative comparison',
    whenToUse: 'Whenever content appears at more than one URL, including pagination and URL parameters.',
  },
  {
    term: '301 Redirect',
    definition: 'A server-level redirect that permanently moves a URL to a new location. Passes approximately 90-99% of link equity to the destination. Stronger canonicalization signal than a canonical tag.',
    formula: 'HTTP status code 301',
    whenToUse: 'When permanently decommissioning a URL and consolidating to a single version.',
  },
  {
    term: 'Link Equity',
    definition: 'The SEO value passed from one page to another through links. Duplicate content splits link equity across multiple URLs, reducing ranking power. Canonical tags consolidate equity to the preferred version.',
    formula: 'Qualitative (PageRank-based)',
    whenToUse: 'Understanding why canonicalization matters for ranking authority.',
  },
  {
    term: 'Hreflang',
    definition: 'An HTML attribute that tells search engines which language and country a page targets. Works alongside canonical tags for multilingual sites to prevent geo-targeted pages from being treated as duplicates.',
    formula: '<link rel="alternate" hreflang="lang-COUNTRY" href="URL">',
    whenToUse: 'Any site serving content in multiple languages or targeting multiple regions.',
  },
];

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const CanonicalUrlCheckerContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free canonical URL checker</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Enter your page URL and
            canonical URL to instantly run 6 canonicalization checks and see exactly what to fix.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>Canonicalization Rules</StyledSectionLabel>
          <StyledSectionHeading>
            What makes a canonical URL correct
          </StyledSectionHeading>
          <StyledSectionIntro>
            A canonical URL is correct when it is an exact, absolute URL match to the preferred
            page version with no conflicting signals from protocol, host, path, or query parameters.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Canonical Match Score</p>
            <p className="formula">Exact match = Good | 1-2 differences = Fair | 3+ differences = Poor</p>
            <p className="example">
              Example: <span>www mismatch + trailing slash difference = 2 issues = Fair. Fix both for exact match.</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              A self-referencing canonical (where the page URL and canonical URL are identical) is
              the gold standard for pages you want indexed. It explicitly declares to search engines
              that this is the preferred and only version of the content, eliminating any ambiguity
              about which URL should receive ranking credit.
            </p>
            <p>
              Non-self-referencing canonicals (where one page declares another page as canonical)
              are valid for consolidating duplicate content. In this case, all six checks still apply:
              the canonical target must be accessible, indexable, and reachable without further redirects.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Common Canonical Issues</StyledSectionLabel>
          <StyledSectionHeading>
            Most common canonical URL conflicts and how to fix them
          </StyledSectionHeading>
          <StyledSectionIntro>
            Use this reference table to diagnose and fix the most frequent canonical mismatches
            found across websites of all sizes.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Issue Type</th>
                  <th>Severity</th>
                  <th>Example</th>
                  <th>Fix</th>
                </tr>
              </thead>
              <tbody>
                {canonicalIssues.map((row) => (
                  <tr key={row.issue}>
                    <td className="col-name">{row.issue}</td>
                    <td>
                      <StyledBadge
                        color={row.status === 'Mismatch' ? '#991b1b' : row.status === 'Minor' ? '#92400e' : row.status === 'Warning' ? '#0369a1' : '#166534'}
                        bg={row.status === 'Mismatch' ? '#fee2e2' : row.status === 'Minor' ? '#fef3c7' : row.status === 'Warning' ? '#e0f2fe' : '#dcfce7'}
                      >
                        {row.status}
                      </StyledBadge>
                    </td>
                    <td className="col-value" style={{ fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: 400 }}>{row.example}</td>
                    <td className="col-note">{row.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Based on Google Search Central canonicalization documentation, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>What Breaks Canonicalization</StyledSectionLabel>
          <StyledSectionHeading>
            Six canonical tag mistakes that confuse search engines
          </StyledSectionHeading>
          <StyledSectionIntro>
            Canonical tag errors are silent ranking killers. These six mistakes cause Google to
            index the wrong page version or ignore your canonical signals entirely.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {canonicalMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Canonicalization Tips</StyledSectionLabel>
          <StyledSectionHeading>
            8 tips to implement canonical tags correctly and prevent future issues
          </StyledSectionHeading>
          <StyledSectionIntro>
            Apply these strategies to build a clean canonical architecture across your entire site.
            All CommonNinja widgets mentioned are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Technical SEO Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Canonical and duplicate content terms explained
          </StyledSectionHeading>
          <StyledSectionIntro>
            Understanding these fundamentals helps you diagnose canonical issues faster and
            explain them clearly to developers and stakeholders.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                  <th>Format / Formula</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {relatedPosts.length > 0 && (
        <StyledSection>
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on canonical URLs and duplicate content</StyledSectionHeading>
            <StyledSectionIntro>
              Go deeper on canonical strategy, URL structure, and the technical SEO practices
              that protect your rankings from duplicate content issues.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard key={post.slug} href={`/blog/${post.slug}`}>
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
